import { useState, useEffect } from 'react';
import axios from 'axios';
import { FiltersBar } from '@/components/FiltersBar';
import { RecommendationsTable } from '@/components/RecommendationsTable';
import { TicketDetailDrawer } from '@/components/TicketDetailDrawer';
import { AIRecommendations } from '@/components/AIRecommendations';
import { EVScatterChart } from '@/components/EVScatterChart';
import { PriceTierBreakdown } from '@/components/PriceTierBreakdown';
import { VelocityTracker } from '@/components/VelocityTracker';
import { AIInsightsPanel } from '@/components/AIInsightsPanel';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { AlertCircle, BarChart3, Brain, Trophy, TrendingUp, Sparkles } from 'lucide-react';
import { useStateContext } from '@/context/StateContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Dashboard = () => {
  const { currentState, getCurrentStateName, getApiUrl } = useStateContext();
  const [rankings, setRankings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    priceTier: 'All',
    rankingMode: 'roi',
    hasJackpot: false,
    minEvPercent: 0,
    age: 365,
    newOnly: false,
    targetPrize: null  // New filter for target prize amount
  });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('insights');
  
  const fetchRankings = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const params = {};
      if (filters.priceTier && filters.priceTier !== 'All') {
        params.price_tier = filters.priceTier;
      }
      if (filters.rankingMode) {
        params.ranking_mode = filters.rankingMode;
      }
      if (filters.hasJackpot) {
        params.has_jackpot = true;
      }
      if (filters.minEvPercent > 0) {
        params.min_ev_percent = filters.minEvPercent;
      }
      
      // Use state-specific endpoint
      const apiUrl = getApiUrl('/rankings');
      const response = await axios.get(apiUrl, { params });
      setRankings(response.data);
      
      if (response.data.length === 0) {
        toast.info(`No games found for ${getCurrentStateName()}`, {
          description: 'Try adjusting your filters or ingesting data for this state'
        });
      }
    } catch (err) {
      console.error('Error fetching rankings:', err);
      setError(err.message);
      toast.error('Failed to load rankings', {
        description: err.message
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchGameDetails = async (gameId) => {
    try {
      // Use legacy API for game details (works for all states)
      const response = await axios.get(`${API}/games/${gameId}`);
      setSelectedTicket(response.data);
      setDrawerOpen(true);
    } catch (err) {
      console.error('Error fetching game details:', err);
      toast.error('Failed to load game details', {
        description: err.message
      });
    }
  };
  
  useEffect(() => {
    fetchRankings();
  }, [filters.priceTier, filters.rankingMode, filters.hasJackpot, filters.minEvPercent, currentState]);
  
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };
  
  const handleReset = () => {
    setFilters({
      priceTier: 'All',
      rankingMode: 'roi',
      hasJackpot: false,
      minEvPercent: 0,
      age: 365,
      newOnly: false,
      targetPrize: null
    });
    toast.success('Filters reset');
  };
  
  const handleRefresh = () => {
    toast.promise(
      fetchRankings(),
      {
        loading: 'Refreshing data...',
        success: 'Data refreshed successfully',
        error: 'Failed to refresh data'
      }
    );
  };
  
  const handleSelectTicket = (ticket) => {
    fetchGameDetails(ticket.game_id);
  };
  
  // Apply client-side filters
  const filteredRankings = rankings.filter(game => {
    if (filters.newOnly && game.status !== 'New') return false;
    if (filters.age < 365 && game.game_age_days && game.game_age_days > filters.age) return false;
    // Filter by target prize - check if game has prizes >= target still available
    if (filters.targetPrize && game.top_prize_amount < filters.targetPrize) return false;
    return true;
  });
  
  // Get ranking mode label
  const getRankingModeLabel = (mode) => {
    const labels = {
      roi: 'Best ROI (EV%)',
      absolute: 'Highest Dollar Value',
      jackpot: 'Jackpot Available',
      upper_tier: 'Most Upper-Tier Prizes'
    };
    return labels[mode] || 'Best ROI';
  };

  // Calculate stats
  const jackpotCount = rankings.filter(g => g.jackpot_available).length;
  const avgROI = rankings.length > 0 
    ? (rankings.reduce((sum, g) => sum + g.ev_percent, 0) / rankings.length).toFixed(1)
    : '0.0';
  const bestROI = rankings.length > 0 
    ? Math.max(...rankings.map(g => g.ev_percent)).toFixed(1)
    : '0.0';
  
  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6" data-testid="dashboard-page">
      {/* Header - Mobile Optimized */}
      <div className="space-y-1">
        <h1 className="text-2xl md:text-4xl font-bold tracking-tight">
          {getCurrentStateName()} <span className="text-accent">Best Tickets</span>
        </h1>
        <p className="text-sm md:text-base text-muted-foreground">
          Data-driven recommendations ranked by {getRankingModeLabel(filters.rankingMode)}
        </p>
      </div>
      
      {/* Filters - Collapsible on mobile */}
      <FiltersBar 
        filters={filters}
        onFilterChange={handleFilterChange}
        onReset={handleReset}
      />
      
      {/* Stats Cards - Mobile Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card>
          <CardContent className="p-3 md:p-5">
            <div className="text-xs md:text-sm text-muted-foreground mb-1">Total Games</div>
            <div className="text-2xl md:text-3xl font-semibold">{rankings.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-5">
            <div className="text-xs md:text-sm text-muted-foreground mb-1 flex items-center gap-1">
              <Trophy className="w-3 h-3" /> Jackpots
            </div>
            <div className="text-2xl md:text-3xl font-semibold">{jackpotCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-5">
            <div className="text-xs md:text-sm text-muted-foreground mb-1">Avg ROI</div>
            <div className="text-2xl md:text-3xl font-semibold">{avgROI}%</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-5">
            <div className="text-xs md:text-sm text-muted-foreground mb-1">Best ROI</div>
            <div className="text-2xl md:text-3xl font-semibold text-success">{bestROI}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs - Mobile Friendly */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-4">
          <TabsTrigger value="insights" className="flex items-center gap-1.5 text-xs md:text-sm">
            <Sparkles className="w-4 h-4" />
            <span>Insights</span>
          </TabsTrigger>
          <TabsTrigger value="ai" className="flex items-center gap-1.5 text-xs md:text-sm">
            <Brain className="w-4 h-4" />
            <span>AI Picks</span>
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-1.5 text-xs md:text-sm">
            <BarChart3 className="w-4 h-4" />
            <span>Charts</span>
          </TabsTrigger>
          <TabsTrigger value="velocity" className="flex items-center gap-1.5 text-xs md:text-sm">
            <TrendingUp className="w-4 h-4" />
            <span>Velocity</span>
          </TabsTrigger>
        </TabsList>

        {/* AI Insights Tab - NEW */}
        <TabsContent value="insights" className="space-y-4">
          <AIInsightsPanel />
        </TabsContent>

        {/* AI Recommendations Tab */}
        <TabsContent value="ai" className="space-y-4">
          <AIRecommendations onSelectGame={(gameId) => fetchGameDetails(gameId)} />
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <EVScatterChart 
              games={filteredRankings} 
              onSelectGame={(gameId) => fetchGameDetails(gameId)}
            />
            <PriceTierBreakdown games={filteredRankings} />
          </div>
        </TabsContent>

        {/* Velocity Tab */}
        <TabsContent value="velocity" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <VelocityTracker 
              games={[...filteredRankings].sort((a, b) => (a.prizes_remaining || 100) - (b.prizes_remaining || 100)).slice(0, 10)} 
              title="🔥 Selling Fast (Most Depleted)"
            />
            <VelocityTracker 
              games={[...filteredRankings].sort((a, b) => (b.prizes_remaining || 0) - (a.prizes_remaining || 0)).slice(0, 10)} 
              title="❄️ Fresh Inventory (Least Depleted)"
            />
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Error State */}
      {error && (
        <Card className="border-danger/50 bg-danger/5">
          <CardContent className="p-4 md:p-5 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-danger mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-danger mb-1">Error Loading Data</div>
              <div className="text-sm text-muted-foreground">{error}</div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Rankings Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <span className="text-lg md:text-xl">Ranked Recommendations</span>
            <span className="text-sm font-normal text-muted-foreground">
              {filteredRankings.length} {filteredRankings.length === 1 ? 'game' : 'games'}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 md:p-6 md:pt-0">
          <div className="overflow-x-auto">
            <RecommendationsTable 
              data={filteredRankings}
              onSelect={handleSelectTicket}
              isLoading={isLoading}
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Detail Drawer */}
      <TicketDetailDrawer 
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        ticket={selectedTicket}
      />
    </div>
  );
};
